import { useCallback, useEffect, useState } from "react";
import { Flame, Check, Package, Disc3, Coins, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { RARITY, clock } from "@/lib/daily";
import ChestReveal from "@/components/ChestReveal";
import SpinWheel from "@/components/SpinWheel";

function Quests({ d }) {
  return (
    <div data-testid="today-quests" className="space-y-1.5">
      <p className="text-[11px] font-mono-game text-slate-400">DAILY QUESTS · {d.quest_credits} CREDITS EACH</p>
      {d.quests.map((q) => (
        <div key={q.id} data-testid={`quest-${q.id}`} data-done={q.done} className={`flex items-center gap-2 text-sm rounded-lg px-2.5 py-1.5 border transition-colors ${q.done ? "border-emerald-600/50 bg-emerald-950/30 text-emerald-200" : "border-slate-800 text-slate-300"}`}>
          {q.done ? <Check className="w-4 h-4 text-emerald-400" /> : <span className="w-4 h-4 rounded-full border border-slate-600" />}
          <span className="flex-1">{q.label}</span>
          <span className="font-mono-game text-xs" data-testid={`quest-progress-${q.id}`}>{q.progress}/{q.goal}</span>
        </div>
      ))}
      <p data-testid="today-all-bonus" className={`text-xs ${d.all_paid ? "text-emerald-300" : "text-slate-500"}`}>{d.all_paid ? `All three done! +${d.all_bonus} bonus paid.` : `Finish all three for +${d.all_bonus} bonus.`}</p>
    </div>
  );
}

function Satchel({ chests, onOpen }) {
  const owned = Object.entries(chests).filter(([, n]) => n > 0);
  return (
    <div data-testid="today-satchel">
      <p className="text-[11px] font-mono-game text-slate-400 mb-1.5">CHEST SATCHEL</p>
      {owned.length ? (
        <div className="flex flex-wrap gap-2">
          {owned.map(([r, n]) => (
            <button key={r} data-testid={`open-chest-${r}`} onClick={() => onOpen(r)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs hover:scale-105 transition-transform" style={{ borderColor: RARITY[r].color, color: RARITY[r].color }}>
              <Package className="w-3.5 h-3.5" />{RARITY[r].label} ×{n} · Open
            </button>
          ))}
        </div>
      ) : <p className="text-xs text-slate-500">No chests yet. Journey wins drop them, and every 5-win streak guarantees one.</p>}
    </div>
  );
}

export default function TodayPanel({ refresh }) {
  const [d, setD] = useState(null);
  const [left, setLeft] = useState(0);
  const [chest, setChest] = useState(null);
  const [spinOpen, setSpinOpen] = useState(false);
  const load = useCallback(() => api.get("/daily").then(({ data }) => { setD(data); setLeft(data.reset_in); }).catch(() => {}), []);

  useEffect(() => { load(); window.addEventListener("daily-changed", load); return () => window.removeEventListener("daily-changed", load); }, [load]);
  useEffect(() => { const t = setInterval(() => setLeft((s) => (s <= 1 ? (load(), 0) : s - 1)), 1000); return () => clearInterval(t); }, [load]);
  const closeChest = useCallback(() => { setChest(null); load(); }, [load]);
  if (!d) return null;
  const pct = Math.min(100, (d.battle_today / d.battle_cap) * 100);

  return (
    <div data-testid="today-panel" className="glass rounded-2xl p-5 space-y-4 rise">
      <div className="flex items-center justify-between">
        <p className="font-display text-xl text-amber-100">Today</p>
        <p data-testid="today-reset-timer" className="text-[11px] font-mono-game text-slate-400">RESETS IN {clock(left)}</p>
      </div>
      <div>
        <div className="flex justify-between text-[11px] font-mono-game text-slate-400 mb-1"><span className="flex items-center gap-1"><Coins className="w-3 h-3 text-amber-300" />BATTLE CREDITS</span>
          <span data-testid="today-battle-earned">{d.battle_today}/{d.battle_cap}</span></div>
        <div className="h-2 rounded-full bg-slate-800 overflow-hidden"><div className="h-full bg-amber-400 transition-[width] duration-700" style={{ width: `${pct}%` }} /></div>
      </div>
      <div className="flex flex-wrap gap-2 text-xs">
        <span data-testid="today-streak" className={`flex items-center gap-1 px-2.5 py-1 rounded-full border ${d.streak >= 3 ? "border-orange-400/70 text-orange-200 bg-orange-500/10 flame-flicker" : "border-slate-700 text-slate-400"}`}>
          <Flame className="w-3.5 h-3.5" />{d.streak}-win streak{d.streak >= 3 ? " · +10% XP" : ""}</span>
        <span data-testid="today-first-win" className={`flex items-center gap-1 px-2.5 py-1 rounded-full border ${d.first_win_ready ? "border-amber-400/70 text-amber-200 nft-glow" : "border-slate-700 text-slate-500"}`}>
          <Sparkles className="w-3.5 h-3.5" />{d.first_win_ready ? "First win: double credits" : "First win bonus used"}</span>
      </div>
      <Quests d={d} />
      <Satchel chests={d.chests} onOpen={setChest} />
      <button data-testid="today-spin-btn" onClick={() => setSpinOpen(true)} className={`w-full flex items-center justify-center gap-2 py-3 rounded-lg border text-sm transition-colors ${d.spins_left ? "border-amber-400/70 text-amber-200 bg-amber-500/10 hover:bg-amber-500/20" : "border-slate-700 text-slate-400"}`}>
        <Disc3 className={`w-4 h-4 ${d.spins_left ? "animate-spin [animation-duration:3s]" : ""}`} />
        {d.spins_left ? `Lucky spin ready (${d.spins_left})` : `Next spin in ${clock(left)}`}
      </button>
      <ChestReveal rarity={chest} onClose={closeChest} refresh={refresh} />
      <SpinWheel open={spinOpen} onClose={() => setSpinOpen(false)} daily={d} refresh={refresh} />
    </div>
  );
}
